/**
 * API Keys Settings Page (BYOK)
 *
 * Lets users bring their own provider API keys. When a key is configured for a
 * provider, generations that use that provider run on the user's key and don't
 * spend Knods credits (see the 🔑 badge on nodes).
 *
 * Free tier is capped at 1 key (enforced both client-side and server-side; the
 * server returns 403 KEY_LIMIT_REACHED). Pro/Corporate are uncapped.
 *
 * Style is cloned from AgentSettingsPage (reuses agent-settings-* classes).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import { useByokKeys } from '../hooks/useByokKeys';
import PageHeader from '../components/PageHeader';
import {
  byokService,
  ByokKeyLimitError,
  BYOK_PROVIDERS,
  BYOK_PROVIDER_LABELS,
  type ByokCredentialSummary,
  type ByokProvider,
} from '../services/ByokService';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import './AgentSettingsPage.css';
import './ApiKeysSettingsPage.css';

const PROVIDER_OPTIONS = BYOK_PROVIDERS.map(p => ({ value: p, label: BYOK_PROVIDER_LABELS[p] }));

export const ApiKeysSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tier } = useCredits();
  const { refresh: refreshByokBadges } = useByokKeys();

  const [keys, setKeys] = useState<ByokCredentialSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<{ provider: ByokProvider; apiKey: string; label: string }>({
    provider: BYOK_PROVIDERS[0],
    apiKey: '',
    label: '',
  });

  // Free tier is capped at a single key.
  const isFree = tier === 'free';
  const atFreeCap = isFree && keys.length >= 1;

  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await byokService.listCredentials();
      setKeys(data);
    } catch (err: any) {
      console.error('Failed to load API keys:', err);
      setError(err.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleAdd = async () => {
    if (!form.apiKey.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await byokService.setCredential(form.provider, form.apiKey.trim(), form.label.trim() || undefined);
      setForm({ provider: BYOK_PROVIDERS[0], apiKey: '', label: '' });
      setShowAdd(false);
      await loadKeys();
      refreshByokBadges(); // update per-node 🔑/💳 badges
    } catch (err: any) {
      if (err instanceof ByokKeyLimitError) {
        setError('Free allows 1 API key — delete it or upgrade to add more.');
      } else {
        setError(err.message || 'Failed to save API key');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (provider: string) => {
    setError(null);
    try {
      await byokService.removeCredential(provider);
      await loadKeys();
      refreshByokBadges();
    } catch (err: any) {
      console.error('Failed to delete API key:', err);
      setError(err.message || 'Failed to delete API key');
    }
  };

  const providerLabel = (p: string): string =>
    BYOK_PROVIDER_LABELS[p as ByokProvider] ?? p;

  return (
    <div className="agent-settings-page">
      <PageHeader />

      <div className="agent-settings-container">
        <div className="agent-settings-header">
          <VpnKeyIcon sx={{ fontSize: 28 }} />
          <h1>API Keys</h1>
        </div>

        <div className="agent-settings-section">
          <div className="section-header">
            <div>
              <h2>Your Provider Keys (BYOK)</h2>
              <p className="section-description">
                Bring your own API keys for AI providers. Nodes that use a provider you've
                configured will run on your key and won't spend Knods credits.
              </p>
            </div>
            <button
              className="agent-add-btn"
              onClick={() => {
                setShowAdd(true);
                setError(null);
                setForm({ provider: BYOK_PROVIDERS[0], apiKey: '', label: '' });
              }}
              disabled={atFreeCap}
              title={atFreeCap ? 'Free allows 1 API key — delete it or upgrade to add more.' : undefined}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Add Key
            </button>
          </div>

          {atFreeCap && (
            <div className="byok-limit-notice">
              <LockIcon sx={{ fontSize: 16 }} />
              <span>
                Free allows 1 API key — delete it or{' '}
                <button className="byok-upgrade-link" onClick={() => navigate('/pricing')}>
                  upgrade
                </button>{' '}
                to add more.
              </span>
            </div>
          )}

          {error && (
            <div className="byok-error-banner">
              <span>{error}</span>
              <button onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          {/* Add Form */}
          {showAdd && (
            <div className="agent-form">
              <div className="agent-form-fields byok-form-fields">
                <div className="form-field">
                  <label>Provider</label>
                  <span className="form-field-hint">Which AI provider this key is for</span>
                  <select
                    value={form.provider}
                    onChange={e => setForm(prev => ({ ...prev, provider: e.target.value as ByokProvider }))}
                  >
                    {PROVIDER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>API Key</label>
                  <span className="form-field-hint">Stored encrypted; never shown again</span>
                  <input
                    type="password"
                    placeholder="sk-..."
                    autoComplete="off"
                    value={form.apiKey}
                    onChange={e => setForm(prev => ({ ...prev, apiKey: e.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label>Label (optional)</label>
                  <span className="form-field-hint">A friendly name for this key</span>
                  <input
                    type="text"
                    placeholder="e.g. Personal OpenAI"
                    value={form.label}
                    onChange={e => setForm(prev => ({ ...prev, label: e.target.value }))}
                  />
                </div>
              </div>
              <div className="agent-form-actions">
                <button className="agent-btn-primary" onClick={handleAdd} disabled={saving || !form.apiKey.trim()}>
                  {saving ? 'Saving...' : 'Save Key'}
                </button>
                <button className="agent-btn-secondary" onClick={() => { setShowAdd(false); setError(null); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Keys List */}
          {loading ? (
            <div className="agent-loading">Loading API keys...</div>
          ) : keys.length === 0 ? (
            <div className="agent-empty">
              <VpnKeyIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              <p>No API keys configured.</p>
              <p className="agent-empty-hint">Add a provider key to run those nodes on your own account.</p>
            </div>
          ) : (
            <div className="api-keys-list">
              {keys.map(key => (
                <div key={key.provider} className="api-key-card">
                  <div className="api-key-info">
                    <div className="api-key-name">{providerLabel(key.provider)}</div>
                    {key.label && key.label !== key.provider && (
                      <div className="api-key-prefix">{key.label}</div>
                    )}
                    <div className="api-key-meta">
                      Updated {new Date(key.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="api-key-actions">
                    <button
                      className="agent-icon-btn danger"
                      onClick={() => handleDelete(key.provider)}
                      title="Delete key"
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiKeysSettingsPage;
